import Swal from "sweetalert2";
import { cityQuestionAPI } from "@/modules/city";

const getValue = (obj, camel, pascal) =>
  obj && obj[camel] !== undefined ? obj[camel] : obj && obj[pascal];

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const normalizar = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const listItems = (items) =>
  (items || []).map((item) => `<li>${escapeHtml(item)}</li>`).join("");

const compactItems = (items, limit = 6) =>
  (items || [])
    .slice(0, limit)
    .map((item) => `<span style="display:inline-block;margin:2px 4px 2px 0;padding:3px 7px;border-radius:4px;background:#f1f1f1;font-size:12px">${escapeHtml(item)}</span>`)
    .join("");

const humanizarTermoAssociado = (item) => {
  const texto = String(item || "").trim();
  if (!texto) return "";

  const normalizado = normalizar(texto);
  if (
    normalizado.startsWith("item minimo:") ||
    normalizado.startsWith("costuma aceitar:") ||
    normalizado.startsWith("costuma recusar:") ||
    normalizado.startsWith("padrao aprovado em producao:")
  ) {
    return "";
  }

  return texto
    .replace(/_/g, " ")
    .replace(/\bmpe\b/gi, "MPE")
    .replace(/\bmei\b/gi, "MEI")
    .replace(/\bepp\b/gi, "EPP")
    .replace(/\blc\b/gi, "LC");
};

const termosAssociadosVisiveis = (items) => {
  const vistos = new Set();
  return (items || [])
    .map(humanizarTermoAssociado)
    .filter((item) => {
      if (!item) return false;
      const chave = normalizar(item);
      if (vistos.has(chave)) return false;
      vistos.add(chave);
      return true;
    })
    .slice(0, 12);
};

const getFileExtension = (name) => {
  const parts = String(name || "").split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

const getFileKey = (file) => `${file.name || ""}|${file.size || 0}`;

const flagHabilitada = (value) =>
  value === true || value === "true" || value === 1 || value === "1";

const analiseIaUploadHabilitada = (config, groupsConfig) =>
  flagHabilitada(config && config.analiseIaUpload) &&
  flagHabilitada(groupsConfig && groupsConfig.analiseIaUpload);

const collectFilePreviews = (formData) => {
  const previews = {};
  const urls = [];
  const files = formData.getAll("arquivos").filter((item) => item instanceof File);

  files.forEach((file) => {
    if (getFileExtension(file.name) !== "pdf") return;

    const url = URL.createObjectURL(file);
    previews[getFileKey(file)] = url;
    previews[file.name] = url;
    urls.push(url);
  });

  return { previews, urls };
};

const getPreviewUrl = (arquivo, previews) => {
  const nome = getValue(arquivo, "nome", "Nome");
  const tamanho = getValue(arquivo, "tamanhoBytes", "TamanhoBytes");
  return previews[`${nome}|${tamanho}`] || previews[nome];
};

const buildRecommendation = (recomendacao) => {
  const texto = recomendacao || "Não recomendado";
  const bruto = String(texto || "").toLowerCase();
  const normalizada = normalizar(texto);
  const negativa = normalizada.includes("nao recomendado") || /n.{0,8}o recomendado/i.test(bruto);
  const recomendado = normalizada.includes("recomendado") && !negativa;
  const background = recomendado ? "#1f9d55" : "#f59e0b";

  return `<span style="display:inline-block;padding:5px 10px;border-radius:4px;background:${background};color:#fff;font-weight:700">${escapeHtml(texto)}</span>`;
};

const buildMunicipioHtml = (analise) => {
  const municipioEsperado = getValue(analise, "municipioEsperado", "MunicipioEsperado");
  if (!municipioEsperado) return "";

  const municipioEncontrado = getValue(analise, "municipioEncontrado", "MunicipioEncontrado");
  const encontrado = getValue(analise, "municipioEsperadoEncontrado", "MunicipioEsperadoEncontrado");
  const background = encontrado ? "#e7f7ed" : "#fff4df";
  const color = encontrado ? "#1f7a3e" : "#9a5b00";
  const texto = encontrado
    ? `Município esperado encontrado no arquivo: ${municipioEsperado}.`
    : municipioEncontrado
    ? `Não encontrei menção a ${municipioEsperado}; encontrei possível menção a ${municipioEncontrado}.`
    : `Não foi possível verificar menções ao município ${municipioEsperado} no arquivo enviado.`;

  return `<p style="padding:8px 10px;border-radius:4px;background:${background};color:${color}"><strong>Município:</strong> ${escapeHtml(texto)}</p>`;
};

const buildArquivosHtml = (arquivos, previews) =>
  (arquivos || [])
    .map((arquivo) => {
      const termos = termosAssociadosVisiveis(getValue(arquivo, "termosEncontrados", "TermosEncontrados") || []);
      const nome = getValue(arquivo, "nome", "Nome");
      const extensao = String(getValue(arquivo, "extensao", "Extensao") || "").toLowerCase();
      const previewUrl = extensao === "pdf" ? getPreviewUrl(arquivo, previews) : null;

      return `
        <div style="margin-top:14px;text-align:left">
          <strong>${escapeHtml(nome)}</strong>
          <div>${escapeHtml(getValue(arquivo, "descricao", "Descricao"))}</div>
          ${
            previewUrl
              ? `<div style="margin-top:8px;border:1px solid #d9d9d9;border-radius:4px;overflow:hidden;background:#f5f5f5">
                   <iframe title="Prévia do PDF enviado" src="${escapeHtml(previewUrl)}#toolbar=0&navpanes=0&scrollbar=0" style="width:100%;height:220px;border:0"></iframe>
                 </div>`
              : ""
          }
          ${
            termos.length
              ? `<div style="margin-top:6px"><strong>Termos associados:</strong> ${escapeHtml(termos.join(", "))}</div>`
              : ""
          }
        </div>`;
    })
    .join("");

const buildRegraHtml = (analise) => {
  return "";

  const titulo = getValue(analise, "regraAceitabilidadeTitulo", "RegraAceitabilidadeTitulo");
  const orientacao = getValue(analise, "orientacaoEvidencia", "OrientacaoEvidencia");
  const itens = getValue(analise, "itensMinimos", "ItensMinimos") || [];
  const aceitas = getValue(analise, "evidenciasAceitas", "EvidenciasAceitas") || [];
  const naoAceitas = getValue(analise, "evidenciasNaoAceitas", "EvidenciasNaoAceitas") || [];
  const sinais = getValue(analise, "sinaisRejeicaoEncontrados", "SinaisRejeicaoEncontrados") || [];

  if (!titulo && !orientacao && !itens.length && !aceitas.length && !naoAceitas.length) return "";

  return `
    <div style="margin:10px 0;padding:10px;border:1px solid #e2e2e2;border-radius:4px;background:#fafafa">
      ${titulo ? `<p style="margin:0 0 6px"><strong>Regra do quesito:</strong> ${escapeHtml(titulo)}</p>` : ""}
      ${orientacao ? `<p style="margin:0 0 8px">${escapeHtml(orientacao)}</p>` : ""}
      ${itens.length ? `<div style="margin-top:6px"><strong>Itens mínimos:</strong><ul style="margin-top:4px">${listItems(itens)}</ul></div>` : ""}
      ${aceitas.length ? `<div style="margin-top:6px"><strong>Costuma aceitar:</strong><br>${compactItems(aceitas)}</div>` : ""}
      ${naoAceitas.length ? `<div style="margin-top:6px"><strong>Costuma recusar:</strong><br>${compactItems(naoAceitas)}</div>` : ""}
      ${
        sinais.length
          ? `<div style="margin-top:8px;padding:7px;background:#fff4df;color:#8a4b00;border-radius:4px"><strong>Sinais encontrados:</strong> ${escapeHtml(sinais.join(", "))}</div>`
          : ""
      }
    </div>`;
};

const buildHtml = (analise, previews) => {
  const pontosAtencao = getValue(analise, "pontosAtencao", "PontosAtencao") || [];
  const arquivos = getValue(analise, "arquivos", "Arquivos") || [];
  const recomendacao = getValue(analise, "recomendacao", "Recomendacao");

  return `
    <div style="text-align:left;color:#444">
      <p><strong>Documento:</strong> ${escapeHtml(getValue(analise, "descricaoDocumento", "DescricaoDocumento"))}</p>
      ${buildMunicipioHtml(analise)}
      <p><strong>Pré-análise técnica:</strong> ${escapeHtml(getValue(analise, "preAnaliseTecnica", "PreAnaliseTecnica"))}</p>
      <p><strong>Recomendação:</strong> ${buildRecommendation(recomendacao)}</p>
      ${
        pontosAtencao.length
          ? `<p><strong>Pontos de atenção:</strong></p><ul>${listItems(pontosAtencao)}</ul>`
          : ""
      }
      ${buildArquivosHtml(arquivos, previews)}
    </div>
  `;
};

export const confirmarAnaliseIaUpload = async (formData, setLoading, config, groupsConfig) => {
  if (!analiseIaUploadHabilitada(config, groupsConfig)) return true;

  const previewState = collectFilePreviews(formData);

  try {
    const { data } = await cityQuestionAPI.preAnaliseUpload(formData);
    const deveExibir = getValue(data, "deveExibir", "DeveExibir");

    if (!deveExibir) return true;

    setLoading(false);
    const result = await Swal.fire({
      title: "Análise IA no Upload",
      html: buildHtml(data, previewState.previews),
      width: 820,
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Prosseguir com envio",
      cancelButtonText: "Não enviar",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      setLoading(true);
      return true;
    }

    return false;
  } catch (error) {
    setLoading(false);
    const result = await Swal.fire({
      title: "Pré-análise indisponível",
      text: "Não foi possível executar a pré-análise agora. Deseja prosseguir com o envio mesmo assim?",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Prosseguir com envio",
      cancelButtonText: "Não enviar",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      setLoading(true);
      return true;
    }

    return false;
  } finally {
    previewState.urls.forEach((url) => URL.revokeObjectURL(url));
  }
};
